import { useEffect, useState } from "react";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { SettingsService } from "../../../services/api";
import { useGlobalContext } from "../../../stores/Global";
import useRefresh from "../../../hooks/useRefresh";
import { useTranslation } from "react-i18next";
import { useMutation, useMutationState } from "@tanstack/react-query";
import { capitalizeFirstLetter } from "../../../helpers/string";
import { useNavigate } from "react-router";
import FieldLabel from "../../atoms/FieldLabel";

interface SMTPResponse {
  key: string;
  message: string;
}

export default function SmtpSettingsPage() {
  const { t } = useTranslation("Settings");
  const { context } = useGlobalContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [server, setServer] = useState("");
  const [port, setPort] = useState(0);
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [from, setFrom] = useState("");

  const [testEmail, setTestEmail] = useState("");

  const [smtpResponse, setSmtpResponse] = useState<SMTPResponse[]>([]);

  const [loading] = useMutationState({
    filters: { mutationKey: ["settings"] },
    select: (mutation) => mutation.state.status === "pending",
  });

  const { mutate: handleTestEmail, isPending: isTestEmailLoading } =
    useMutation({
      mutationFn: async () => {
        const res = await SettingsService.createSmtpTest({ testEmail });
        return res?.data?.data;
      },
      onSuccess: (data) => {
        if (typeof data === "object") {
          const arr: SMTPResponse[] = [];
          Object.keys(data).forEach((key) => {
            let message = "";
            const info = (data as any)[key];
            if (Array.isArray(info)) message = info.join(", ");
            else if (typeof info === "string") message = info;
            if (message) arr.push({ key: capitalizeFirstLetter(key), message });
          });
          setSmtpResponse(arr);
        }
      },
    });

  const handleSubmit = async () => {
    if (!context.settings) return;
    await SettingsService.updateSettings({
      appSettings: {
        ...context.settings,
        smtp: {
          enabled,
          server,
          from,
          username,
          password,
          port,
          secure,
        },
      },
    });
    refresh();
  };

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!context.settings) return;
    setEnabled(context.settings.smtp.enabled);
    setServer(context.settings.smtp.server);
    setFrom(context.settings.smtp.from);
    setUsername(context.settings.smtp.username);
    setPassword(context.settings.smtp.password);
    setSecure(context.settings.smtp.secure);
    setPort(context.settings.smtp.port);
  }, [context.settings]);

  return (
    <FormTemplate
      title={t("SMTP.Title")}
      disableCancelButton
      submitButtonText={t("Save")}
      disableEnter
      onSubmit={handleSubmit}
      onCancel={handleClose}
      confirm
      loading={loading}
    >
      <Grid container alignItems={"center"} rowGap={3}>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Enabled")} />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            field="enabled"
            label={""}
            type="switch"
            value={enabled}
            onChange={(value) => setEnabled(value as boolean)}
          />
        </Grid>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Server")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="server"
            label={""}
            placeholder={t("SMTPServerPlaceholder")}
            type="string"
            value={server}
            onChange={(value) => setServer(value.toString())}
          />
        </Grid>
        <Grid size={{ md: 1 }}>
          <Box />
        </Grid>
        <Grid size={{ xs: 5, md: 2 }}>
          <FieldLabel children={t("Port")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="port"
            label={""}
            type="number"
            value={Number.isInteger(port) ? port : 0}
            onChange={(value) => setPort(parseInt(value.toString()))}
          />
        </Grid>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Username")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="username"
            label={""}
            type="string"
            value={username}
            onChange={(value) => setUsername(value.toString())}
          />
        </Grid>
        <Grid size={{ md: 1 }}>
          <Box />
        </Grid>
        <Grid size={{ xs: 5, md: 2 }}>
          <FieldLabel children={t("FromEmail")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="from"
            label={""}
            type="string"
            value={from}
            onChange={(value) => setFrom(value.toString())}
          />
        </Grid>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Password")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="password"
            label={""}
            type="string"
            inputType="password"
            value={password}
            onChange={(value) => setPassword(value.toString())}
          />
        </Grid>
        <Grid size={{ md: 1 }}>
          <Box />
        </Grid>
        <Grid size={{ xs: 5, md: 2 }}>
          <FieldLabel children={t("TestEmail")} />
        </Grid>
        <Grid size={{ xs: 7, md: 3 }}>
          <FormInput
            loading={loading}
            field="testEmail"
            label={""}
            placeholder={t("TestEmailPlaceholder")}
            type="string"
            value={testEmail}
            onChange={(value) => setTestEmail(value as string)}
          />
        </Grid>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("SecureConnection")} required />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            field="secure"
            label={""}
            type="switch"
            value={secure}
            onChange={(value) => setSecure(value as boolean)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Button
            sx={{ minWidth: 150, width: "fit-content" }}
            onClick={() => handleTestEmail()}
            variant="outlined"
            loading={isTestEmailLoading}
          >
            {t("Test")}
          </Button>
        </Grid>
        {smtpResponse.length > 0 && (
          <Grid size={12}>
            <Divider />
          </Grid>
        )}
        {smtpResponse.length > 0 && (
          <Grid size={12}>
            <Box p={2} bgcolor="background.paper">
              {smtpResponse.map((res) => (
                <Typography color="primary">
                  {res.key}
                  {res.key && ":"}
                  <Typography component="span" color="success">
                    {" " + res.message}
                  </Typography>
                </Typography>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </FormTemplate>
  );
}
